import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Box, useTheme } from "@mui/material";
import { $getNodeByKey, type NodeKey } from "lexical";
import {
  useContext,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

import { OfferLabelContext, OfferListContext } from "./offerLabelContext";
import { $isOfferNode } from "./OfferNode";
import type { OfferDefinition } from "./OfferNode";
import { OfferPickerPopover } from "../../../InlineLexicalInput/pickers/OfferPickerPopover";

import { EditOutlinedIcon } from "@/icons";

interface OfferChipProps {
  nodeKey: NodeKey;
  slug: string;
  /** Label baked into the node; used when no live resolver is provided. */
  label: string;
}

export function OfferChip({ nodeKey, slug, label }: OfferChipProps) {
  const theme = useTheme();
  const [editor] = useLexicalComposerContext();
  const resolveLabel = useContext(OfferLabelContext);
  const offerList = useContext(OfferListContext);
  const displayLabel = resolveLabel?.(slug) ?? label;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const interactive = offerList.length > 0;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (!interactive) return;
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!interactive) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setAnchorEl(event.currentTarget as HTMLElement);
    }
  };

  const handleSelect = (offer: OfferDefinition) => {
    setAnchorEl(null);
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isOfferNode(node)) {
        node.setOffer(offer.slug, offer.preview, offer.fallbackCode);
      }
    });
  };

  return (
    <>
      <Box
        component="span"
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive ? `Change offer ${displayLabel}` : undefined}
        aria-haspopup={interactive ? "dialog" : undefined}
        onClick={interactive ? handleOpen : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
        sx={{
          display: "inline",
          verticalAlign: "baseline",
          backgroundColor: theme.palette.success.bgContainer,
          color: theme.palette.success.main,
          borderRadius: `${theme.shape.borderRadiusSm}px`,
          px: "6px",
          boxDecorationBreak: "clone",
          fontSize: "inherit",
          lineHeight: "inherit",
          fontWeight: 500,
          userSelect: "none",
          cursor: interactive ? "pointer" : "default",
          whiteSpace: "nowrap",
          "&:hover": interactive
            ? { backgroundColor: theme.palette.success.light }
            : undefined,
          "&:focus-visible": interactive
            ? {
                outline: `2px solid ${theme.palette.success.main}`,
                outlineOffset: "1px",
              }
            : undefined,
        }}
      >
        {interactive ? (
          <EditOutlinedIcon
            sx={{
              fontSize: "14px",
              verticalAlign: "middle",
              mr: "4px",
              position: "relative",
              top: "-1px",
            }}
          />
        ) : null}
        {displayLabel}
      </Box>
      {interactive ? (
        <OfferPickerPopover
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          offers={offerList}
          onSelectOffer={handleSelect}
        />
      ) : null}
    </>
  );
}
